import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { addPorcentage } from "../../../utils/formatter";
import InfoIcon from "../../../assets/icons/info-icon.svg";
import FinanceCardChart from "../financeCardChart/financeCardChart";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const FinanceDeliquency = ({ delinquencies = [], dateRange = null, code }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [clickedMonthData, setClickedMonthData] = useState(null);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const chartRef = useRef(null);
  const navigate = useNavigate();

  const data = {
    labels: delinquencies.map((d) => d.month),
    datasets: [
      {
        label: "Taxa de Inadimplência Mensal",
        data: delinquencies.map((d) => d.delinquencyRate),
        borderColor: "#0962AD",
        backgroundColor: "#0962AD",
        borderWidth: 2,
        yAxisID: "y1" // Porcentagem
      },
      {
        label: "Taxa de Adimplência Mensal",
        data: delinquencies.map((d) => d.payingRate),
        borderColor: "#2BAD70",
        backgroundColor: "#2BAD70",
        borderWidth: 2,
        yAxisID: "y1" // Porcentagem
      },
      {
        label: "Valor em atraso",
        data: delinquencies.map((d) => d.dueValue),
        borderColor: "#FFA500",
        backgroundColor: "#FFA500",
        borderWidth: 2,
        yAxisID: "y2" // Monetário
      }
    ]
  };

  const handleChartClick = (event, elements) => {
    if (elements && elements.length > 0) {
      const clickedElement = elements[0];
      const datasetIndex = clickedElement.datasetIndex;
      
      // Verificar se clicou no dataset "Taxa de Inadimplência Mensal" (índice 0)
      if (datasetIndex === 0) {
        const pointIndex = clickedElement.index;
        const monthRaw = delinquencies[pointIndex].month;
        
        console.log('Mês de inadimplência clicado:', monthRaw);
        
        setClickedMonthData({
          raw: monthRaw,
          formatted: formatMonthDisplay(monthRaw)
        });
        setModalPosition({
          x: event.native.clientX,
          y: event.native.clientY
        });
        setShowDataModal(true);
      }
    }
  };

  const formatMonthDisplay = (monthStr) => {
    if (!monthStr) return '';
    
    if (monthStr.includes('/')) {
      return monthStr;
    }
    
    const parts = monthStr.split('-');
    if (parts.length === 2) {
      const [year, month] = parts;
      const monthNames = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
      ];
      const monthIndex = parseInt(month) - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        return `${monthNames[monthIndex]}/${year}`;
      }
    }
    
    return monthStr;
  };

  const handleNavigateToData = () => {
    if (!clickedMonthData) {
      setShowDataModal(false);
      return;
    }
    
    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('filterId', '7d1feb06-6655-4ef3-889c-a8130499ad3d');
    
    // Adicionar datas do dateRange se disponíveis
    if (dateRange) {
      if (dateRange.from) {
        const startDate = dateRange.from;
        const formattedStartDate = `${String(startDate.getDate()).padStart(2, '0')}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${startDate.getFullYear()}`;
        params.set('startDate', formattedStartDate);
      }
      
      if (dateRange.to) {
        const endDate = dateRange.to;
        const formattedEndDate = `${String(endDate.getDate()).padStart(2, '0')}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${endDate.getFullYear()}`;
        params.set('endDate', formattedEndDate);
      }
    }
    
    console.log('Navegando para inadimplentes com datas:', params.toString());
    
    navigate(`/app/dados-consolidados?${params.toString()}`);
    setShowDataModal(false);
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: handleChartClick,
    plugins: {
      legend: {
        display: true,
        position: "top",
        align: "end",
        labels: {
          boxHeight: 6,
          color: "#858585",
          font: { size: 14 }
        }
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.dataset.label;
            const value = context.raw;

            if (context.dataset.yAxisID === 'y2') {
              return `${label}: R$ ${value.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
              })}`;
            }

            return `${label}: ${addPorcentage(value)}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false }
      },
      y1: {
        type: 'linear',
        position: 'left',
        ticks: {
          callback: (value) => addPorcentage(value)
        },
        title: {
          display: true,
          text: 'Porcentagem',
        }
      },
      y2: {
        type: 'linear',
        position: 'right',
        grid: { drawOnChartArea: false },
        ticks: {
          callback: (value) =>
            `R$ ${value.toLocaleString('pt-BR', {
              minimumFractionDigits: 2
            })}`
        },
        title: {
          display: true,
          text: 'Valor Monetário (R$)',
        }
      }
    }
  };

  return (
    <>
      <FinanceCardChart
        code={code}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>Inadimplência</span>
            <div style={{ position: 'relative' }}>
              <img
                src={InfoIcon}
                alt="Informação"
                onClick={() => setShowPopover(!showPopover)}
                style={{
                  width: '14px',
                  height: '14px',
                  cursor: 'pointer',
                  verticalAlign: 'middle',
                  display: 'inline-block',
                }}
              />
              {showPopover && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "100%",
                    transform: "translate(0, -50%)",
                    marginLeft: "8px",
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    padding: "10px",
                    zIndex: 100,
                    width: "220px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    borderRadius: "4px"
                  }}
                >
                  <p style={{ margin: 0, fontSize: "12px", fontWeight: "normal" }}>
                    O cálculo da inadimplência mês a mês soma o valor total de parcelas pagas (em atrasos e em dia) e divide pelo valor total de parcelas vencidas no mês.
                  </p>
                  <button
                    onClick={() => setShowPopover(false)}
                    style={{
                      marginTop: "8px",
                      fontSize: "10px",
                      cursor: "pointer",
                      background: "none",
                      border: "none",
                      color: "#0962AD"
                    }}
                  >
                    Fechar
                  </button>
                </div>
              )}
            </div>
          </div>
        }
      >
        <Line ref={chartRef} data={data} options={options} />
      </FinanceCardChart>

      {showDataModal && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
            onClick={() => setShowDataModal(false)}
          />
          <div
            style={{
              position: "fixed",
              top: modalPosition.y + 10,
              left: modalPosition.x - 100,
              backgroundColor: "#fff",
              padding: "12px 16px",
              borderRadius: "6px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              border: "1px solid #e0e0e0",
              zIndex: 1000,
              minWidth: "200px",
              maxWidth: "250px"
            }}
          >
            <p style={{ margin: "0 0 12px 0", fontSize: "13px", color: "#333", fontWeight: "500" }}>
              Inadimplentes - {clickedMonthData?.formatted || ''}
            </p>
            <button
              onClick={handleNavigateToData}
              style={{
                width: "100%",
                padding: "6px 12px",
                fontSize: "13px",
                cursor: "pointer",
                backgroundColor: "#0962AD",
                border: "none",
                borderRadius: "4px",
                color: "#fff"
              }}
            >
              Ver dados consolidados
            </button>
          </div>
        </>
      )}
    </>
  );
};

export default FinanceDeliquency;
